import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberAsMemberNew,
  useMutationPayInvoiceAsMember,
  useQueryGetInvoiceByInvoiceNumberAsMember,
} from "@gymflow/api";
import { cn, formatCurrency } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { FormikProvider, useFormik } from "formik";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, FormPaymentMethodPicker, Spinner } from "../../atoms";
import DashedIcon from "../../atoms/icons/DashedIcon";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import {
  INITIAL_VALUES_OVERDUE_PAYMENT_FORM,
  overduePaymentFormSchema,
} from "./constants";
import { OverduePaymentFormValues } from "./types";

export const HostedPagesOverduePayment = () => {
  const { t } = useTranslation();
  const { isEmbed } = usePortalRoutes();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const [parent] = useAutoAnimate();
  const { api } = useGymflowModels();
  const { defaultCurrency } = useClubSettings();
  const { notifyDanger } = useContext(ToastContext);
  const history = useHistory();
  const showScaModal = useScaModal({
    asMember: true,
  });
  const { invoiceNumber } = useParams<{
    invoiceNumber?: string;
  }>();

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const { data: userMember, isFetching: isFetchingUserMember } =
    useMemberAsMemberNew({ api });

  const { mutateAsync: mutateAsyncPayInvoice } = useMutationPayInvoiceAsMember({
    api,
  });

  const { data: dataInvoice, isFetching: isFetchingInvoice } =
    useQueryGetInvoiceByInvoiceNumberAsMember({
      api,
      invoiceNumber: invoiceNumber ?? "",
      enabled: !!invoiceNumber,
    });

  const formik = useFormik<OverduePaymentFormValues>({
    initialValues: INITIAL_VALUES_OVERDUE_PAYMENT_FORM,
    validationSchema: toFormikValidationSchema(overduePaymentFormSchema),
    onSubmit: async ({ paymentMethodId }) => {
      if (!paymentMethodId || !invoiceNumber) {
        return;
      }
      try {
        const newInvoice = await mutateAsyncPayInvoice({
          invoiceNumber,
          paymentMethodId,
        });
        showScaModal({
          invoiceNumber: newInvoice.number ?? invoiceNumber,
        });
        history.replace(createSiteOrEmbedLink(RouteFeature.SiteProfile));
      } catch (e) {
        if (isAxiosError(e)) {
          notifyDanger(e);
        }
      }
    },
  });

  const amountDue = formatCurrency(
    dataInvoice?.dueAmount ?? 0,
    defaultCurrency,
  );

  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("page.hostedPagesOverduePayment.header")}
      />
      <div
        ref={parent}
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-900 px-4 py-6",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isFetchingUserMember || isFetchingInvoice ? (
          <Spinner />
        ) : (
          <div className="flex w-full max-w-lg flex-col gap-10">
            <FormikProvider value={formik}>
              <div className="flex flex-col gap-7">
                {userMember && (
                  <FormPaymentMethodPicker<OverduePaymentFormValues>
                    name="paymentMethodId"
                    userMemberId={userMember.id}
                    customLabel={t(
                      "page.hostedPagesOverduePayment.form.paymentMethodId",
                    )}
                    asMember
                    allowBankPayment
                  />
                )}
                <DashedIcon pathClassName="dark:stroke-gray-700" />
                <div className="flex justify-between">
                  <div className="dark:text-gray-25 text-xl font-bold text-gray-950">
                    {t("page.hostedPagesOverduePayment.amountDue")}
                  </div>
                  <div className="dark:text-gray-25 text-xl font-bold text-gray-950">
                    {amountDue}
                  </div>
                </div>
              </div>
              <Button
                intent="secondary"
                className="w-56 self-center"
                onClick={async () => await formik.submitForm()}
              >
                {t("page.hostedPagesOverduePayment.form.pay", {
                  amount: amountDue,
                })}
              </Button>
            </FormikProvider>
          </div>
        )}
      </div>
    </div>
  );
};
